import { useEffect, useState, useRef } from 'react';
import { animated, useSpring, useSprings } from '@react-spring/web';
import { sendTutorMessage } from '../services/ai';

import { useTranslation } from 'react-i18next';

import { AiFillHome } from 'react-icons/ai';
import { IoIosHelpCircle } from 'react-icons/io';
import { IoIosChatboxes } from 'react-icons/io';
import { IoSend } from 'react-icons/io5';
import { TbCircleLetterD } from 'react-icons/tb';
import { IoIosArrowBack } from 'react-icons/io';
import logo from '../../assets/image/logo_resized.png';

function Home({ setSelectedPage, course_name }) {
    const { t } = useTranslation();

    const springs = useSpring({
        from: { background: 'linear-gradient(180deg, rgba(11, 68, 117, 1) 2%, rgba(255, 255, 255, 1) 0%)' },
        to: { background: 'linear-gradient(180deg, rgba(11, 68, 117, 1) 2%, rgba(255, 255, 255, 1) 100%)' },
        config: { duration: 400 },
    });

    return (
        <animated.div style={{ ...springs }} id="home-container">
            <TbCircleLetterD id="D-icon-home" />
            <div id="home-header">
                <span>{t('chatbot.hiThere')}👋</span>
                <span>{t('tutor.howCanIHelp')}</span>

                <div id="ask-a-question" onClick={() => setSelectedPage('Messanger')}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{t('chatbot.askAQuestion')}</span>
                        <span>
                            {t('tutor.getHelp')} {course_name}
                        </span>
                    </div>

                    <IoSend id="ask-a-question-icon" />
                </div>
            </div>

            <div id="chatbot-nav">
                <AiFillHome className="nav-icon" onClick={() => setSelectedPage('Home')} />
                <IoIosChatboxes className="nav-icon" onClick={() => setSelectedPage('Messanger')} />
            </div>
        </animated.div>
    );
}

// Status lines shown while waiting on the tutor. Generation genuinely takes
// 10-60s, so a static placeholder reads as a hung request. These are ordered to
// roughly track what the server is actually doing — retrieval first, then
// composition — rather than being pure decoration.
//
// Each entry is [translation key, English fallback]. The fallbacks mean this
// works before the keys are added to the locale files.
const THINKING_PHRASES = [
    ['tutor.thinking.reading', 'Reading your question'],
    ['tutor.thinking.searching', 'Searching the lecture notes'],
    ['tutor.thinking.gathering', 'Gathering relevant material'],
    ['tutor.thinking.connecting', 'Connecting the concepts'],
    ['tutor.thinking.composing', 'Composing an answer'],
    ['tutor.thinking.checking', 'Checking it over'],
];

function ThinkingIndicator() {
    const { t } = useTranslation();

    const [phraseIndex, setPhraseIndex] = useState(0);
    const [dots, setDots] = useState('');

    useEffect(() => {
        // Slow enough to read, quick enough to feel alive. A full cycle is
        // about 13s, so a long generation loops rather than freezing.
        const phraseTimer = setInterval(() => {
            setPhraseIndex((i) => (i + 1) % THINKING_PHRASES.length);
        }, 2200);

        return () => clearInterval(phraseTimer);
    }, []);

    useEffect(() => {
        const dotTimer = setInterval(() => {
            setDots((d) => (d.length >= 3 ? '' : `${d}.`));
        }, 400);

        return () => clearInterval(dotTimer);
    }, []);

    const [key, fallback] = THINKING_PHRASES[phraseIndex];

    return (
        <span style={{ fontStyle: 'italic', opacity: 0.75 }}>
            {t(key, fallback)}
            {/* Fixed width stops the bubble resizing as dots come and go. */}
            <span style={{ display: 'inline-block', width: '1.2em', textAlign: 'left' }}>{dots}</span>
        </span>
    );
}

function Messanger({ setSelectedPage, course_id, course_name }) {
    const { t } = useTranslation();

    const messagesRef = useRef();

    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: `${t('tutor.introduction')} ${course_name}?`,
            id: Date.now(),
        },
    ]);
    const [userInput, setUserInput] = useState('');
    const [loading, setLoading] = useState(false);

    const backgroundSprings = useSpring({
        from: { background: 'linear-gradient(180deg, rgba(11, 68, 117, 1) 2%, rgba(255, 255, 255, 1) 100%)' },
        to: { background: 'linear-gradient(180deg, rgba(11, 68, 117, 1) 2%, rgba(255, 255, 255, 1) 0%)' },
        config: { duration: 100 },
    });

    const [springs, api] = useSprings(
        messages.length,
        () => ({
            to: { opacity: 1, transform: 'scale(1)' },
            from: { opacity: 0, transform: 'scale(0.3)' },
            config: { tension: 250, friction: 20 },
        }),
        []
    );

    function Message({ message, index }) {
        return (
            <animated.div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    ...springs[index],
                    width: '100%',
                    justifyContent: message.role != 'assistant' ? 'flex-end' : 'flex-start',
                    paddingRight: message.role != 'assistant' ? '10px' : '0px',
                }}
            >
                {message.role == 'assistant' ? <img src={logo} className="message-ai-img" /> : null}
                <div className="message" style={message.role != 'assistant' ? { background: 'rgb(0, 123, 255)' } : {}}>
                    {message.pending ? (
                        <ThinkingIndicator />
                    ) : (
                        <span style={{ textAlign: 'left' }}>{message.content}</span>
                    )}
                </div>
            </animated.div>
        );
    }

    function handleInputChange(e) {
        setUserInput(e.target.value);
    }

    async function submitUserInput() {
        if (userInput == '') {
            return;
        }

        const question = userInput;
        setUserInput('');
        setLoading(true);

        // Show the question and a placeholder immediately, then swap the
        // placeholder for the reply once it arrives.
        setMessages((prev) => [
            ...prev,
            { role: 'user', content: question, id: Date.now() },
            { role: 'assistant', content: '', pending: true, id: Date.now() + 1 },
        ]);

        try {
            // The conversation is resolved server-side from the authenticated
            // user, so there is no thread or conversation id to track here.
            // The reply arrives already stripped of citations and document
            // references.
            const resp = await sendTutorMessage(course_id, question);
            setMessages((prev) => [
                ...prev.slice(0, -1),
                { role: 'assistant', content: resp.data.reply, id: Date.now() },
            ]);
        } catch (err) {
            console.error(err);
            setMessages((prev) => [
                ...prev.slice(0, -1),
                {
                    role: 'assistant',
                    content: t('tutor.error', 'Sorry, something went wrong. Please try again.'),
                    id: Date.now(),
                },
            ]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setTimeout(() => (messagesRef.current.scrollTop = messagesRef.current.scrollHeight), 10);
    }, [messages]);


    return (
        <animated.div style={{ ...backgroundSprings }} id="help-container">
            <div id="help-nav">
                {/* <IoIosArrowBack id='back-arrow' onClick={() => setSelectedPage('Home')} /> */}
                <span>DAWRATY</span>
            </div>

            <div id="messages-container">
                <div id="help-header">
                    <TbCircleLetterD id="D-icon-help" />
                    <span>{t('chatbot.askAQuestion')}</span>
                    <span>
                        {t('tutor.getHelp')} {course_name}
                    </span>
                </div>

                <div
                    ref={messagesRef}
                    style={{
                        overflowY: 'scroll',
                        overflowX: 'hidden',
                        height: '390px',
                        borderBottom: '1px solid rgba(128, 128, 128, 0.388)',
                    }}
                >
                    {messages.map((message, key) => (
                        <Message message={message} key={key} index={key} />
                    ))}
                </div>

                <input
                    value={userInput}
                    onKeyUp={(e) => {
                        if (e.key == 'Enter') {
                            submitUserInput();
                        }
                    }}
                    onChange={(e) => handleInputChange(e)}
                    disabled={loading}
                    style={{ backgroundColor: loading ? 'grey' : 'white' }}
                    placeholder="Message..."
                    id="help-input"
                />
            </div>
        </animated.div>
    );
}

function TutorRevamp({ course_id, course_name }) {
    // NOTE(ai-migration): `Home` is unreachable — `fetchPage` has been
    // commented out for some time and `Messanger` is rendered directly. Left in
    // place rather than deleted, since reviving the landing page is a product
    // decision, not migration work.
    const [selectedPage, setSelectedPage] = useState('Home');

    return (
        <div id="chatbot-container">
            <Messanger setSelectedPage={setSelectedPage} course_id={course_id} course_name={course_name} />
        </div>
    );
}

export default TutorRevamp;
